import React, { useState, useEffect, useCallback, useRef } from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { ModalService } from 'ui-components/services/modal-service';
import { ExportRecordsModal } from '..';
import { ErrorModal } from '../../error-modal';
import { GenericErrorRetryContainer } from '../../../../containers/generic-error-retry-container';
import { ExportOptionsValues } from '../enums/export-options-enum';
import { DefaultExportFormat } from '../enums/default-export-format';
import { getSavedExportFormats, deleteExportFormat } from '../api';
import { LogService } from '../../../../services/log-service';
import { messages } from '../messages/general-export-messages';

const ExportRecordsModalWrapper = ({
  modalFrameworkMethods,
  intl,
  exportTypeEnum,
  onInitiateExport,
  onUpdateExportType,
  recordExportType,
  numberRecordsSelected,
  numberRecordsInView,
  retryAction,
}) => {
  const [exportFormats, setExportFormats] = useState([]);
  const formatsForType = [DefaultExportFormat, ...(exportFormats || []).filter((format) => format.extractType === exportTypeEnum)];
  const [shouldLoadFormats, setShouldLoadFormats] = useState(true);
  const { closeModal } = modalFrameworkMethods;
  const mountedRef = useRef(true);

  const updateExportFormats = useCallback(
    (savedFormats) => {
      setExportFormats([...savedFormats]);
    },
    [setExportFormats],
  );

  const onSaveExportFormat = useCallback(() => {
    setShouldLoadFormats(true);
  }, [setShouldLoadFormats]);

  const onDeleteExportFormat = useCallback(
    (formatId) => {
      deleteExportFormat(formatId)
        .then(() => {
          if (!mountedRef.current) {
            return;
          }
          setShouldLoadFormats(true);
        })
        .catch((error) => {
          LogService.logError(new Error('Error deleting export format', { cause: error }));
          // TODO: As part of intl modal cleanup, we can use <FormattedMessage /> here
          ModalService.launchModal({
            component: ErrorModal,
            props: {
              title: intl.formatMessage(messages.errorTitle),
              message: intl.formatMessage(messages.deleteError),
            },
          });
        });
    },
    [intl],
  );

  useEffect(() => {
    if (shouldLoadFormats) {
      getSavedExportFormats()
        .then((data) => {
          if (!mountedRef.current) {
            return;
          }
          updateExportFormats(data);
          setShouldLoadFormats(false);
        })
        .catch((error) => {
          if (!mountedRef.current) {
            return;
          }
          LogService.logError(new Error('Error fetching saved export formats', { cause: error }));
          closeModal();
          setShouldLoadFormats(false);
          // TODO: As part of intl modal cleanup, we can use <FormattedMessage /> here
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action: retryAction(),
              title: intl.formatMessage(messages.errorTitle),
              message: intl.formatMessage(messages.retrieveSavedFormatsError),
            },
          });
        });
    }
  }, [shouldLoadFormats, exportTypeEnum, closeModal, intl, updateExportFormats, retryAction]);

  useEffect(() => {
    return () => (mountedRef.current = false);
  }, []);

  return (
    <ExportRecordsModal
      closeModal={closeModal}
      exportFormats={formatsForType}
      numberRecordsInView={numberRecordsInView}
      numberRecordsSelected={numberRecordsSelected}
      onDeleteExportFormat={onDeleteExportFormat}
      onExportTypeChanged={onUpdateExportType}
      onInitiateExport={onInitiateExport}
      onSaveExportFormat={onSaveExportFormat}
      recordExportType={recordExportType}
    />
  );
};

ExportRecordsModalWrapper.propTypes = {
  modalFrameworkMethods: PropTypes.object.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  exportTypeEnum: PropTypes.oneOf(ExportOptionsValues).isRequired,
  recordExportType: PropTypes.oneOf(ExportOptionsValues).isRequired,
  onInitiateExport: PropTypes.func.isRequired,
  onUpdateExportType: PropTypes.func.isRequired,
  numberRecordsInView: PropTypes.number.isRequired,
  numberRecordsSelected: PropTypes.number.isRequired,
  retryAction: PropTypes.func.isRequired,
};

export const ExportRecordsContainer = injectIntl(ExportRecordsModalWrapper);
